import type { Date as PDate } from "../../generated/core/v1/date";
import type { Family } from "../../generated/core/v1/family";
import type { FinancialPlanData } from "../../generated/core/v1/financial_plan";
import { compareDates, fromIso, monthsBetween } from "../lib/dates";
import {
  debtComparison,
  householdRetirement,
  incomeSummary,
  leftOverMonthly,
  LOAN_LABEL,
  paycheckSplit,
  priorityOutcomes,
  type RetirementInfo,
  type ShortfallView,
  shortfallView,
  spendingByCategory,
  startDate,
  endDate,
} from "../plan-view";

/**
 * A decision's answer (v38's module output), from engine facts only: the scenario's plan against
 * the household's plan today (the stored plan's Abi run), both read through plan-view. Nothing here
 * is a financial rule; the verdict only compares what the engine reported for the two plans.
 *
 * The verdict rule (Ian, 2026-10-31):
 * - when the scenario runs short of cash:
 *   - **no** (`shortfall`) if today's plan doesn't, or runs short in a later month, or in the same
 *     month by less (the scenario's gap is larger, by at least a dollar as the page shows it);
 *   - **trade** (`either`) otherwise: today's plan already runs short and the scenario's shortfall is
 *     no sooner and no larger (later, smaller, both, or unchanged). Nothing else changes the colour;
 * - when it doesn't (whether or not today's plan does):
 *   - **no** (`lost`) if a priority today's plan funds gets nothing at all;
 *   - **trade** (`priority`, `retirement`) if any priority (retirement's nest egg included) loses at
 *     least a percentage point of funding, or retirement moves at least a month later;
 *   - **yes** (`none`) otherwise.
 *
 * The engine stops a plan at its first cash shortfall, so a plan that runs short ends there. Every
 * figure compares the two plans over the same years only: up to `until`, the earlier of the two
 * shortfalls (when either runs short). Monthly figures average at most the first year from the
 * decision, cut at `until`; priorities, retirement, interest, and debt-free dates are read from both
 * plans cut at `until`, and a priority neither plan reaches before it isn't compared.
 */
export type Verdict = "yes" | "trade" | "no";

/** A priority's funding (0..1, capped at fully funded) in each plan. */
export type PriorityChange = {
  key: string;
  name: string;
  importance: number;
  isRetirement: boolean;
  before: number;
  after: number;
};

export type DecisionFacts = {
  verdict: Verdict;
  /** Why the verdict is what it is. */
  reason: "shortfall" | "either" | "lost" | "priority" | "retirement" | "none";
  /**
   * Both plans run short and the scenario's shortfall is no sooner and no larger (`either`): how it
   * compares with today's. Undefined otherwise.
   */
  either?: {
    /** It runs short in a later month. */
    later: boolean;
    /** It's short by less in its shortfall month than today's plan is in its own (MEANINGFUL). */
    smaller: boolean;
    /** It's short by more, in its later month (MEANINGFUL). */
    larger: boolean;
  };
  /** The start of the window the monthly figures average over (the decision's date). */
  from?: PDate;
  /**
   * Where the comparison stops: the earlier of the two plans' first shortfalls (the engine stops a
   * plan there). Undefined when neither runs short.
   */
  until?: PDate;
  /** Whole months the monthly figures average over: twelve, or fewer when `until` comes first. */
  months: number;
  /** Priorities neither plan reaches before `until`, so not compared (not in `priorities`). */
  notCompared: string[];
  shortfall?: ShortfallView;
  shortfallBefore?: ShortfallView;
  priorities: PriorityChange[];
  /** The priority that loses the most funding (at least a point), if any. */
  givesWay?: PriorityChange;
  /** A funded priority that gets nothing in the scenario. */
  lost?: PriorityChange;
  /** The priority that gains the most (at least a point), if any. */
  improves?: PriorityChange;
  retirement: { before?: RetirementInfo; after?: RetirementInfo; monthsLater: number };
  /** Left after the bills, a month, over the window (`months` from the decision). */
  leftOver: { before: number; after: number };
  /** Take-home income, a month, over the window. */
  takeHome: { before: number; after: number };
  debt: {
    /** Debt payments (minimum and extra), a month, over the window. */
    paymentBefore: number;
    paymentAfter: number;
    /** Interest over the plan, or up to `until` (to the end of the month before it). */
    interestBefore: number;
    interestAfter: number;
    /** When debt is cleared, if before `until`. */
    freeBefore?: PDate;
    freeAfter?: PDate;
  };
};

/** A percentage point: the smallest change in a priority's funding that counts. */
export const POINT = 0.01;

/**
 * The smallest change in a dollar figure that counts: a dollar, as the page shows it (amounts are
 * shown to the dollar, so anything smaller can't be seen; and the engine is deterministic, so any
 * difference it reports comes from the decision).
 */
export const MEANINGFUL = 1;

/** `a` exceeds `b` by at least MEANINGFUL, both rounded to the dollar as shown. */
const exceeds = (a: number, b: number) => Math.round(a) - Math.round(b) >= MEANINGFUL;

const funded = (v: number | undefined) => Math.min(1, Math.max(0, v ?? 0));

/**
 * The date a decision takes effect, from the tool's saved form values: an expense's first payment,
 * a purchase date, an insurance event's date, or the earliest job start or end. Undefined if none.
 */
export function decisionDate(inputs: Record<string, unknown> | undefined): PDate | undefined {
  if (!inputs) return undefined;
  const iso: string[] = [];
  const take = (v: unknown) => typeof v === "string" && v && iso.push(v);
  ["dueDate", "purchaseDate", "date"].forEach((k) => take(inputs[k]));
  const rows = (k: string) => (Array.isArray(inputs[k]) ? (inputs[k] as unknown[]) : []);
  rows("offers").forEach((o) => take((o as Record<string, unknown>)?.startDate));
  rows("existing").forEach((j) => {
    const r = j as Record<string, unknown>;
    if (r?.action && r.action !== "keep") take(r.endDate);
  });
  const dates = iso.map(fromIso).filter((d): d is PDate => !!d);
  return dates.sort(compareDates)[0];
}

/** The window's start: the decision's date, kept inside both plans (and before `until`). */
function windowStart(
  baseline: FinancialPlanData,
  scenario: FinancialPlanData,
  date: PDate | undefined,
  until: PDate | undefined,
): PDate | undefined {
  const start = startDate(scenario) ?? startDate(baseline);
  if (!date || !start || compareDates(date, start) < 0) return start;
  const end = until ?? endDate(scenario);
  return end && compareDates(date, end) > 0 ? start : date;
}

/** Whole months from `from` up to `until`, at most twelve. */
function windowMonths(from: PDate | undefined, until: PDate | undefined): number {
  if (!from || !until) return 12;
  const whole = monthsBetween(from, until) - (until.day < from.day ? 1 : 0);
  return Math.max(0, Math.min(12, whole));
}

/**
 * The plan up to `until`: events before it, and month-end states before its month (so both plans
 * are read at the same month-end; the plan's first state is always kept).
 */
function cutAt(plan: FinancialPlanData, until: PDate | undefined): FinancialPlanData {
  if (!until) return plan;
  const monthStart = { year: until.year, month: until.month, day: 1 };
  return {
    ...plan,
    monthlyStates: plan.monthlyStates.filter(
      (s, i) => i === 0 || compareDates(s.date, monthStart) < 0,
    ),
    events: plan.events.filter((e) => compareDates(e.date, until) < 0),
  };
}

function debtPayments(plan: FinancialPlanData, months: number, from: PDate | undefined): number {
  return months ? paycheckSplit(spendingByCategory(plan, months, from)).debt : 0;
}

/** Income sources (entity uuids) that pay once in the what-if and never in today's plan. */
export function oneOffIncomes(
  baseline: FinancialPlanData,
  scenario: FinancialPlanData,
): Set<string> {
  const count = (p: FinancialPlanData) => {
    const n = new Map<string, number>();
    p.events.forEach((e) => {
      if (e.details.oneofKind === "income" && e.entityUuid)
        n.set(e.entityUuid, (n.get(e.entityUuid) ?? 0) + 1);
    });
    return n;
  };
  const before = count(baseline);
  return new Set(
    [...count(scenario)].filter(([u, n]) => n === 1 && !before.has(u)).map(([u]) => u),
  );
}

export function decisionFacts(
  family: Family,
  baseline: FinancialPlanData,
  scenario: FinancialPlanData,
  date?: PDate,
): DecisionFacts {
  const shortfall = shortfallView(scenario);
  const shortfallBefore = shortfallView(baseline);
  // Sooner is an earlier month; within the same month, the amount short (as shown) decides.
  const monthOf = (x: PDate) => ({ year: x.year, month: x.month, day: 0 });
  const byMonth =
    shortfall && shortfallBefore
      ? compareDates(monthOf(shortfall.date), monthOf(shortfallBefore.date))
      : 0;
  const larger =
    !!shortfall && !!shortfallBefore && exceeds(shortfall.amount, shortfallBefore.amount);
  const smaller =
    !!shortfall && !!shortfallBefore && exceeds(shortfallBefore.amount, shortfall.amount);
  const runsShort = !!shortfall && (!shortfallBefore || byMonth < 0 || (byMonth === 0 && larger));
  const either =
    shortfall && !runsShort
      ? { later: byMonth > 0, smaller, larger: byMonth > 0 && larger }
      : undefined;
  const until = [shortfall?.date, shortfallBefore?.date]
    .filter((x): x is PDate => !!x)
    .sort(compareDates)[0];
  const from = windowStart(baseline, scenario, date, until);
  const months = windowMonths(from, until);
  const before = cutAt(baseline, until);
  const after = cutAt(scenario, until);

  // priorityOutcomes(family, a, b) reads `a` as "abi" and `b` as "naive": here, scenario and today.
  const outcomes = priorityOutcomes(family, after, before);
  const reached = until ? outcomes.filter((o) => o.abi || o.naive) : outcomes;
  const priorities: PriorityChange[] = reached.map((o) => ({
    key: o.key,
    name: o.name,
    importance: o.importance,
    isRetirement: o.isRetirement,
    before: funded(o.naive?.completion),
    after: funded(o.abi?.completion),
  }));
  const notCompared = outcomes.filter((o) => !reached.includes(o)).map((o) => o.name);
  const drops = priorities
    .filter((p) => p.before - p.after >= POINT)
    .sort((a, b) => b.before - b.after - (a.before - a.after));
  const gains = priorities
    .filter((p) => p.after - p.before >= POINT)
    .sort((a, b) => b.after - b.before - (a.after - a.before));
  const lost = drops.find((p) => p.before >= POINT && p.after < POINT);

  const rBefore = householdRetirement(before, family);
  const rAfter = householdRetirement(after, family);
  const monthsLater = rBefore && rAfter ? monthsBetween(rBefore.date, rAfter.date) : 0;

  // A one-off payment the what-if adds (an insurance payout) is a lump, not monthly money: leave it
  // out of what's left each month, or a $750,000 payout reads as $60,000 a month more.
  const lumps = oneOffIncomes(baseline, scenario);
  const leftOver = {
    before: months ? leftOverMonthly(baseline, months, from, lumps) : 0,
    after: months ? leftOverMonthly(scenario, months, from, lumps) : 0,
  };
  const reason: DecisionFacts["reason"] = runsShort
    ? "shortfall"
    : either
      ? "either"
      : lost
        ? "lost"
        : drops.length
          ? "priority"
          : monthsLater >= 1
            ? "retirement"
            : "none";
  const verdict: Verdict =
    reason === "shortfall" || reason === "lost" ? "no" : reason === "none" ? "yes" : "trade";

  const dBefore = debtComparison(before);
  const dAfter = debtComparison(after);
  const perMonth = (pick: (p: FinancialPlanData) => number) => ({
    before: months ? pick(baseline) : 0,
    after: months ? pick(scenario) : 0,
  });
  return {
    verdict,
    reason,
    either,
    from,
    until,
    months,
    notCompared,
    shortfall,
    shortfallBefore,
    priorities,
    givesWay: drops[0],
    lost,
    improves: gains[0],
    retirement: { before: rBefore, after: rAfter, monthsLater },
    leftOver,
    takeHome: perMonth((p) => incomeSummary(p, months, from).net),
    debt: {
      paymentBefore: debtPayments(baseline, months, from),
      paymentAfter: debtPayments(scenario, months, from),
      interestBefore: dBefore.lifetimeInterest,
      interestAfter: dAfter.lifetimeInterest,
      freeBefore: dBefore.debtFree,
      freeAfter: dAfter.debtFree,
    },
  };
}

/**
 * The household's dearest credit card or loan with a balance (a selection from the family
 * document), to set a financed purchase's rate against: v38's "dearer than the card you're paying
 * off".
 */
export function dearestDebt(family: Family): { name: string; rate: number } | undefined {
  const owed = (b: number | undefined) => (b ?? 0) > 0.5;
  const all = [
    ...family.persons.flatMap((p) =>
      p.creditCards
        .filter((c) => owed(c.balance) && c.interest !== undefined)
        .map((c) => ({ name: c.name.trim() || "credit card", rate: c.interest! })),
    ),
    ...family.loans
      .filter((l) => owed(l.balance) && l.interest !== undefined)
      .map((l) => ({
        name: l.name.trim() || (LOAN_LABEL[l.type] ?? "loan").toLowerCase(),
        rate: l.interest!,
      })),
  ];
  return all.sort((a, b) => b.rate - a.rate)[0];
}
